/* eslint-disable no-control-regex */
// Partner codes are the stable, rename-safe identifiers shared across the
// frontend, backend, and DB. Imported here and re-exported below so existing
// imports from this module keep working. (SN-838)
import {
  PARTNER_SRT_CODE_SUFFIX,
  PARTNER_AM_CODE_SUFFIX,
  PARTNER_TIER_CODE_SUFFIXES,
  THE_HOME_DEPOT_CODE,
  HD_SUPPLY_CODE,
  WINSUPPLY_CODE,
  RINNAI_CODE,
  CAPITAL_WRAPS_CODE,
  COAST_CODE,
  SPARTAN_TOOL_CODE,
  WESUPPLYTRADES_CODE,
  SUNBELT_RENTALS_CODE,
  EVERCONNECT_CODE,
  POSTCARDMANIA_CODE,
  BEHR_CODE,
  BAYTEK_CODE,
  GUSTO_CODE,
  HDX_CODE,
  NOVO_CODE,
  GOODMAN_DAIKIN_AMANA_CODE,
  RUUD_CODE,
  RHEEM_CODE,
  LENNOX_RESIDENTIAL_CODE,
  LENNOX_COMMERCIAL_CODE,
} from '@optimus/utils/src/constant';

export const CompanyInformationQuestionTag = 'company info';
export const PersonalInformationQuestionTag = 'personal info';
export const CompanyRoleQuestionTag = 'company role';
export const AccountInformationQuestionTag = 'account info';
export const BusinessInformationQuestionTag = 'business info';
export const PartnersInformationQuestionTag = 'partners info';
export const JoistSolutionOrgName = 'joist';
export const ServiceFusionSolutionOrgName = 'service-fusion';
export const InvoiceSimpleSolutionOrgName = 'invoice-simple';
export const RoofsnapSolutionOrgName = 'roofsnap';
export const KickservSolutionOrgName = 'kickserv';
export const MarketSharpSolutionOrgName = 'marketsharp';
export const ImproveIt360SolutionOrgName = 'improve-it-360';
export const EverProEdgePublicSolutionOrgName = 'everpro-edge-public';
export const ServiceNationSolutionOrgName = 'service-nation';
export const THE_HOME_DEPOT_NAME = 'The Home Depot';
export const HOME_DEPOT_NAME = 'HomeDepot';
export const WINSUPPLY_NAME = 'Winsupply';
export const RINNAI_NAME = 'Rinnai';
export const HD_SUPPLY_NAME = 'HD Supply';
export const CAPITAL_WRAPS_NAME = 'Capital Wraps';
export const COAST_NAME = 'Coast';
export const SPARTAN_TOOL_NAME = 'Spartan Tool';
export const WESUPPLYTRADES_NAME = 'WeSupplyTrades';
export const WE_SUPPLY_TRADES_ACCOUNT_URL =
  'https://www.wesupplytrades.com/MyAccount/CreateAccount';
export const SUNBELT_RENTALS_NAME = 'Sunbelt Rentals';
export const EVERCONNECT_NAME = 'EverConnect';
export const POSTCARDMANIA_NAME = 'PostcardMania';
export const BEHR_NAME = 'Behr';
export const BAYTEK_NAME = 'Baytek';
export const GUSTO_NAME = 'Gusto';
export const HDX_NAME = 'HDX';
export const NOVO_NAME = 'NOVO Professional Water Systems';
export const GOODMAN_DAIKIN_AMANA_NAME = 'Daikin Comfort Manufacturing- Goodman, Amana & Daikin HVAC Equipment';
export const RUUD_NAME = 'Ruud';
export const RHEEM_NAME = 'Rheem Manufacturing';
export const LENNOX_RESIDENTIAL_NAME = 'Lennox Residential';
export const LENNOX_COMMERCIAL_NAME = 'Lennox Commercial';
export const PARTNER_NAMES = [
  HOME_DEPOT_NAME,
  WINSUPPLY_NAME,
  HD_SUPPLY_NAME,
  CAPITAL_WRAPS_NAME,
  COAST_NAME,
  SPARTAN_TOOL_NAME,
  WESUPPLYTRADES_NAME,
  SUNBELT_RENTALS_NAME,
  EVERCONNECT_NAME,
  RINNAI_NAME,
  POSTCARDMANIA_NAME,
  BEHR_NAME,
  BAYTEK_NAME,
  GUSTO_NAME,
  HDX_NAME,
  NOVO_NAME,
] as const;
export type PartnerName = typeof PARTNER_NAMES[number];

// Re-export the partner codes so existing imports from this module keep working.
export {
  PARTNER_SRT_CODE_SUFFIX,
  PARTNER_AM_CODE_SUFFIX,
  PARTNER_TIER_CODE_SUFFIXES,
  THE_HOME_DEPOT_CODE,
  HD_SUPPLY_CODE,
  WINSUPPLY_CODE,
  RINNAI_CODE,
  CAPITAL_WRAPS_CODE,
  COAST_CODE,
  SPARTAN_TOOL_CODE,
  WESUPPLYTRADES_CODE,
  SUNBELT_RENTALS_CODE,
  EVERCONNECT_CODE,
  POSTCARDMANIA_CODE,
  BEHR_CODE,
  BAYTEK_CODE,
  GUSTO_CODE,
  HDX_CODE,
  NOVO_CODE,
  GOODMAN_DAIKIN_AMANA_CODE,
  RUUD_CODE,
  RHEEM_CODE,
  LENNOX_RESIDENTIAL_CODE,
  LENNOX_COMMERCIAL_CODE,
};

// Canonical display name per partner code. i18n keys, test IDs, and analytics
// event names are derived by camel/pascal-casing these canonical names (e.g.
// `hdSupply.signUpDialog.*`, `HdSupplySignupForm`). Deriving from the code (a
// stable identifier) instead of the live Retool name keeps those keys and event
// names correct even after a partner is renamed. The canonical names equal the
// live names today, so the derived slugs are unchanged by this mapping.
// SN-838 follow-up. NOTE: srt-tier codes carry the PARTNER_SRT_CODE_SUFFIX, so
// callers should use getCanonicalPartnerName(), which strips it before lookup.
const canonicalPartnerNameByCode: { [code: string]: string } = {
  // Use HOME_DEPOT_NAME ('HomeDepot'), not THE_HOME_DEPOT_NAME ('The Home Depot'),
  // so derived slugs/events stay 'HomeDepot…' (e.g. the historical
  // `HomeDepotWebsite` / `HomeDepotSignupForm` analytics event names are preserved).
  [THE_HOME_DEPOT_CODE]: HOME_DEPOT_NAME,
  [HD_SUPPLY_CODE]: HD_SUPPLY_NAME,
  [WINSUPPLY_CODE]: WINSUPPLY_NAME,
  [RINNAI_CODE]: RINNAI_NAME,
  [CAPITAL_WRAPS_CODE]: CAPITAL_WRAPS_NAME,
  [COAST_CODE]: COAST_NAME,
  [SPARTAN_TOOL_CODE]: SPARTAN_TOOL_NAME,
  [WESUPPLYTRADES_CODE]: WESUPPLYTRADES_NAME,
  [SUNBELT_RENTALS_CODE]: SUNBELT_RENTALS_NAME,
  [EVERCONNECT_CODE]: EVERCONNECT_NAME,
  [POSTCARDMANIA_CODE]: POSTCARDMANIA_NAME,
  [BEHR_CODE]: BEHR_NAME,
  [BAYTEK_CODE]: BAYTEK_NAME,
  [GUSTO_CODE]: GUSTO_NAME,
  [HDX_CODE]: HDX_NAME,
  [NOVO_CODE]: NOVO_NAME,
  [GOODMAN_DAIKIN_AMANA_CODE]: GOODMAN_DAIKIN_AMANA_NAME,
  [RUUD_CODE]: RUUD_NAME,
  [RHEEM_CODE]: RHEEM_NAME,
  [LENNOX_RESIDENTIAL_CODE]: LENNOX_RESIDENTIAL_NAME,
  [LENNOX_COMMERCIAL_CODE]: LENNOX_COMMERCIAL_NAME,
};

/**
 * Normalize a partner code to its base (edge-tier) code by stripping the tier
 * suffix. A partner can have one row per membership tier (edge / association /
 * srt); a non-edge row's code carries that tier's suffix, one of
 * PARTNER_TIER_CODE_SUFFIXES. Use this before matching a code against the
 * *_CODE constants so every tier resolves identically.
 *
 * NOTE: this mirrors `getBasePartnerCode` in `@optimus/utils`, re-implemented
 * here because that package can't be imported by the frontend: its barrel pulls
 * in the axios-heavy ServiceNationClient, and the deep-path source file is
 * parsed as plain JS by CRA's webpack, which rejects its typed signature.
 */
export const getBasePartnerCode = (partnerCode: string): string => {
  const suffix = PARTNER_TIER_CODE_SUFFIXES.find((s) => partnerCode.endsWith(s));
  return suffix ? partnerCode.slice(0, -suffix.length) : partnerCode;
};

/**
 * Resolve the canonical, rename-stable display name for a partner code, used to
 * derive i18n keys / test IDs / analytics event names. Strips the srt-tier
 * suffix so both membership tiers map to the same name. Falls back to
 * `fallbackName` (typically the live API name) for codes with no canonical
 * mapping, preserving today's behaviour for unmapped partners.
 */
export const getCanonicalPartnerName = (
  partnerCode: string | undefined,
  fallbackName = '',
): string => {
  if (!partnerCode) {
    return fallbackName;
  }
  return canonicalPartnerNameByCode[getBasePartnerCode(partnerCode)] ?? fallbackName;
};

// Brand background/label colors for "Shop at {partner} and earn" buttons (SN-879).
// Applies to every shop-and-earn button in the app, not just the distributor list:
// the single-partner button on a partner's own overview page, and Home Depot's
// completed-status shop button both use this same lookup so all three stay in sync.
// Colors per SN-879 acceptance criteria; any partner not listed keeps the button's
// default (unstyled) primary variant.
export const PARTNER_SHOP_BUTTON_BRAND_STYLE: {
  [code: string]: { backgroundColor: string; color: string }
} = {
  [WINSUPPLY_CODE]: { backgroundColor: '#0277BD', color: '#FFFFFF' },
  [WESUPPLYTRADES_CODE]: { backgroundColor: '#1B5E20', color: '#FFFFFF' },
  [SPARTAN_TOOL_CODE]: { backgroundColor: '#D32F2F', color: '#FFFFFF' },
  [COAST_CODE]: { backgroundColor: '#3D5AFE', color: '#FFFFFF' },
  [SUNBELT_RENTALS_CODE]: { backgroundColor: '#2E7D32', color: '#FFFFFF' },
  [HD_SUPPLY_CODE]: { backgroundColor: '#FFB300', color: '#000000' },
  [THE_HOME_DEPOT_CODE]: { backgroundColor: '#E64A19', color: '#FFFFFF' },
  [BAYTEK_CODE]: { backgroundColor: '#0277BD', color: '#FFFFFF' },
  [GUSTO_CODE]: { backgroundColor: '#BF360C', color: '#FFFFFF' },
  [POSTCARDMANIA_CODE]: { backgroundColor: '#C51162', color: '#FFFFFF' },
  [CAPITAL_WRAPS_CODE]: { backgroundColor: '#F9A825', color: '#000000' },
  [EVERCONNECT_CODE]: { backgroundColor: '#3949AB', color: '#FFFFFF' },
};

/**
 * Resolve the brand background/label color for a partner's shop-and-earn button.
 * Strips the srt-tier suffix so both membership tiers resolve identically.
 * Returns undefined for partners with no defined brand style.
 */
export const getPartnerShopButtonBrandStyle = (
  partnerCode: string | undefined,
): { backgroundColor: string; color: string } | undefined => {
  if (!partnerCode) {
    return undefined;
  }
  return PARTNER_SHOP_BUTTON_BRAND_STYLE[getBasePartnerCode(partnerCode)];
};
export const OnboardingEventType = 'onboarding';
export const SignUpEventType = 'signup';
export const ContentEventType = 'content';
export const FeedEventType = 'feed';
export const SearchEventType = 'search';
export const roleAtCompany = 'Role at company';
export const WalletPayout = 'payout';
export const WalletReward = 'reward';
export type SolutionOrgName = typeof JoistSolutionOrgName |
  typeof ServiceFusionSolutionOrgName |
  typeof InvoiceSimpleSolutionOrgName |
  typeof RoofsnapSolutionOrgName |
  typeof KickservSolutionOrgName |
  undefined
export const emailRegex = /^([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22))*\x40([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d))*$/gm;
export const SRTCode = 'SRT';
export const JoistCode = 'JOIST';
export const JOIST_MEMBER_PARTNER_RELATION_EMAIL_RESPONSE = 'JOIST_MEMBER_PARTNER_RELATION_EMAIL_RESPONSE';
export const JOIST_EVENT_BUFFER_RESPONSE = 'JOIST_EVENT_BUFFER_RESPONSE';
export const JOIST_READY_FOR_EVENT_BUFFER_REQUEST = 'JOIST_READY_FOR_EVENT_BUFFER_REQUEST';
export const OPTIMUS_READY_FOR_EVENT_BUFFER_RESPONSE = 'OPTIMUS_READY_FOR_EVENT_BUFFER_RESPONSE';
export const OPTIMUS_EVENT_FOR_EXTERNAL_LINK = 'OPTIMUS_EVENT_FOR_EXTERNAL_LINK';
export const JOIST_COMPLETE_REWARDS_EVERPRO_SIGNUP = 'JOIST_COMPLETE_REWARDS_EVERPRO_SIGNUP';
export const REWARDS_PARTNER_CODE_COMPLETED = 'rewards_partner_code_completed';
export const REWARDS_HOME_DEPOT_SIGN_IN_COMPLETED = 'rewards_home_depot_sign_in_completed';
export const REWARDS_CREDIT_CARD_COMPLETED = 'rewards_credit_card_completed';
export const numberLength9Regex = /^[0-9]{9}$/gm;
export const bankAccountNumberRegex = /^(?![0-]+$)[0-9-]{3,17}$/gm;

export const MARKETO_SERVICE_FUSION_BASE_URL = '//lp.servicefusion.com';
export const MARKETO_SERVICE_FUSION_MUNCHKIN_ID = '516-ZLM-498';
export const MARKETO_SERVICE_FUSION_FORM_ID = 1742;
export const PARTNER_LOGO_IMAGE_WIDTH = 10;

export const COACHING_CONSULT_URL =
  'https://meetings-na2.hubspot.com/michael-rudowitz/membership-consult';
export const PILLAR_CALLS_URL = 'https://connect.servicenation.com/s/core-business-discussions/';

// Membership tier pricing configuration
export const MEMBERSHIP_TIER_PRICING = {
  srt: {
    monthly: 124.95,
    annual: 103.95,
  },
} as const;

// HD URLs
export const HD_SITE_URL = 'https://www.homedepot.com/myaccount/proXtraPricing';
export const HD_SIGN_IN_VIDEO_URL =
  'https://d2o7bp9oz3gksv.cloudfront.net/sign_in_flow_044a3d8ae3.mp4';
export const HD_SIGN_UP_VIDEO_URL =
  'https://d2o7bp9oz3gksv.cloudfront.net/sign_up_flow_beef0edde8.mp4';
